import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
    AlertCircle, ArrowLeft, Check, ChevronDown, ChevronRight, ClipboardList,
    FolderOpen, GripVertical, Loader2, Package, Plus, Save, Search, Tag, Trash2, X,
} from 'lucide-react';
import { useDownloadAttachment, useLoadHttpClientEmpty } from '../../../App.hooks';


/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type ContentTypeReference = { contentId: string; typeName: string };
type MappingReference = string | ContentTypeReference;

/** category → keyName → content type references */
type KeyScheduleMapping = Record<string, Record<string, MappingReference[]>>;

const isContentTypeReference = (value: MappingReference): value is ContentTypeReference =>
    typeof value !== 'string' && !!value?.contentId && !!value?.typeName;
const getReferenceKey = (value: MappingReference) =>
    typeof value === 'string' ? `legacy:${value}` : `type:${value.contentId}:${value.typeName}`;

// Shared drag state — readable during dragover (dataTransfer.getData is blocked then)
let currentDragCategory: string | null = null;

/* ------------------------------------------------------------------ */
/*  GraphQL                                                            */
/* ------------------------------------------------------------------ */

const GET_PROJECT_KEY_SCHEDULE_DATA = gql`
    query getProjectKeyScheduleData($id: String) {
        Project(id: $id) {
            dynamicData {
                id
                slug
                name
                type
                data
                setData
            }
        }
    }
`;

const SET_PROJECT_DYNAMIC_DATA_NAME = gql`
    mutation setProjectDataMappingName($projectId: String, $id: String, $name: String) {
        Project(id: $projectId) {
            setContentDataMappingName(id: $id, name: $name)
        }
    }
`;

const GET_LIBRARY_PICKER_CONTENTS = gql`
    query getLibraryPickerContents($libraryId: String, $search: String, $after: String, $sourceLibraryIds: [String]) {
        Library(id: $libraryId) {
            contents(
                after: $after
                where: {
                    and: [
                        {
                            or: [
                                { name: { contains: $search } }
                                { category: { contains: $search } }
                            ]
                        }
                        { libraryId: { in: $sourceLibraryIds } }
                    ]
                }
                order: { createdTime: DESC }
            ) {
                nodes {
                    id
                    name
                    category
                    currentVersionId
                    libraryId
                    library { name }
                    types { id name }
                }
                pageInfo {
                    hasNextPage
                    endCursor
                }
            }
        }
    }
`;

const GET_LIBRARY_SOURCE_LIBRARIES = gql`
    query getLibrarySourceLibraries($libraryId: String) {
        Library(id: $libraryId) {
            contents(first: 1000) {
                nodes {
                    libraryId
                    library {
                        name
                    }
                }
            }
        }
    }
`;

const GET_VERSION_CATEGORIES = gql`
    query getVersionCategories($ids: [String]) {
        ContentVersionList(where: { id: { in: $ids } }) {
            nodes {
                id
                category
            }
        }
    }
`;

const GET_MAPPED_ITEM_NAMES = gql`
    query getMappedItemNames($libraryId: String) {
        Library(id: $libraryId) {
            contents(first: 10000) {
                nodes {
                    id
                    name
                    currentVersionId
                    types {
                        id
                        name
                    }
                }
            }
        }
    }
`;

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

type KeyScheduleMappingProps = {
    projectId: string;
    libraryId?: string;
    mappingId: string;
    mappingName: string;
    canEdit?: boolean;
    style?: CSSProperties;
    onBack: () => void;
};

/* ------------------------------------------------------------------ */
/*  Library content picker — drag source for the right pane          */
/* ------------------------------------------------------------------ */

type LibraryContentPickerProps = { libraryId: string };

const LibraryContentPicker = ({ libraryId }: LibraryContentPickerProps) => {
    const [searchText, setSearchText] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedLibraryIds, setSelectedLibraryIds] = useState<string[]>([]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchText), 300);
        return () => clearTimeout(t);
    }, [searchText]);

    const { data, loading, fetchMore } = useQuery(GET_LIBRARY_PICKER_CONTENTS, {
        variables: {
            libraryId,
            search: debouncedSearch,
            after: null,
            sourceLibraryIds: selectedLibraryIds.length > 0 ? selectedLibraryIds : null,
        },
        skip: !libraryId,
        fetchPolicy: 'cache-first',
    });

    // Separate lightweight query to enumerate ALL source libraries without pagination
    const { data: sourcesData } = useQuery(GET_LIBRARY_SOURCE_LIBRARIES, {
        variables: { libraryId },
        skip: !libraryId,
        fetchPolicy: 'cache-first',
    });

    const allItems = (data?.Library?.contents?.nodes ?? []).filter(Boolean) as any[];
    const pageInfo = data?.Library?.contents?.pageInfo;

    // Batch-fetch current version categories (Content.category is null; it lives on ContentVersion)
    const [categoryByVersionId, setCategoryByVersionId] = useState<Map<string, string>>(new Map());
    const [fetchVersionCategories] = useLazyQuery(GET_VERSION_CATEGORIES);

    useEffect(() => {
        const versionIds = allItems
            .map((item: any) => item.currentVersionId)
            .filter((id: any): id is string => !!id && !categoryByVersionId.has(id));
        if (versionIds.length === 0) return;
        fetchVersionCategories({ variables: { ids: versionIds } })
            .then(result => {
                const nodes = (result.data?.ContentVersionList?.nodes ?? []) as Array<{ id?: string | null; category?: string | null } | null>;
                setCategoryByVersionId(prev => {
                    const next = new Map(prev);
                    for (const node of nodes) {
                        if (node?.id && node?.category) next.set(node.id, node.category);
                    }
                    return next;
                });
            })
            .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allItems]);

    // Derive unique source libraries from the full (non-paginated) sources query
    const sourceLibraries = useMemo(() => {
        const nodes = (sourcesData?.Library?.contents?.nodes ?? []) as Array<{ libraryId?: string | null; library?: { name?: string | null } | null } | null>;
        const seen = new Map<string, string>();
        for (const item of nodes) {
            if (item?.libraryId && item.library?.name && !seen.has(item.libraryId)) {
                seen.set(item.libraryId, item.library.name);
            }
        }
        return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
    }, [sourcesData]);

    const items = allItems.flatMap((content: any) =>
        (content.types ?? [])
            .filter((type: any) => !!type?.id)
            .map((type: any) => ({
                ...content,
                id: type.id,
                contentId: content.id,
                typeName: type.name,
                name: `${content.name} · ${type.name}`,
            })),
    );

    const toggleLibrary = (id: string) => {
        setSelectedLibraryIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
        );
    };

    const handleDragStart = (e: React.DragEvent, reference: ContentTypeReference, contentName: string, contentCategory: string) => {
        currentDragCategory = contentCategory || null;
        e.dataTransfer.setData('application/x-content-reference', JSON.stringify(reference));
        e.dataTransfer.setData('application/x-content-name', contentName);
        e.dataTransfer.effectAllowed = 'copy';
    };

    const handleDragEnd = () => { currentDragCategory = null; };

    return (
        <div className="flex flex-col h-full gap-2">
            {/* Search */}
            <div className="relative flex-shrink-0">
                <Search
                    size={13}
                    className="absolute top-1/2 -translate-y-1/2 pointer-events-none"
                    style={{ left: 10, color: 'var(--text-tertiary)' }}
                />
                <input
                    type="text"
                    className="input-base w-full"
                    style={{ paddingLeft: 28 }}
                    placeholder="Search name or category…"
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                />
            </div>

            {/* Source library filter chips */}
            {sourceLibraries.length > 0 && (
                <div className="flex flex-wrap gap-1.5 flex-shrink-0">
                    {sourceLibraries.map(lib => {
                        const active = selectedLibraryIds.includes(lib.id);
                        return (
                            <button
                                key={lib.id}
                                onClick={() => toggleLibrary(lib.id)}
                                className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full transition-colors"
                                style={{
                                    backgroundColor: active ? 'var(--accent)' : 'var(--surface-1)',
                                    color: active ? '#fff' : 'var(--text-secondary)',
                                    border: `1px solid ${active ? 'var(--accent)' : 'var(--border-default)'}`,
                                    cursor: 'pointer',
                                }}
                            >
                                {lib.name}
                            </button>
                        );
                    })}
                    {selectedLibraryIds.length > 0 && (
                        <button
                            onClick={() => setSelectedLibraryIds([])}
                            className="text-xs px-2 py-1 rounded-full transition-colors"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', textDecoration: 'underline' }}
                        >
                            Clear
                        </button>
                    )}
                </div>
            )}

            {/* Results */}
            {loading && allItems.length === 0 ? (
                <div className="flex items-center justify-center flex-1 py-8">
                    <Loader2 size={18} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                </div>
            ) : items.length === 0 ? (
                <div className="flex flex-col items-center justify-center flex-1 py-8 gap-2">
                    <Package size={28} style={{ color: 'var(--text-tertiary)', opacity: 0.4 }} />
                    <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No content found</p>
                </div>
            ) : (
                <div className="flex-1 overflow-auto">
                    <div className="grid grid-cols-2 gap-2">
                        {items.map((item: any) => {
                            const itemCategory = categoryByVersionId.get(item.currentVersionId) ?? '';
                            return (
                            <div
                                key={item.id}
                                draggable
                                onDragStart={e => handleDragStart(e, { contentId: item.contentId, typeName: item.typeName }, item.name ?? '', itemCategory)}
                                onDragEnd={handleDragEnd}
                                className="card flex flex-col gap-1.5 p-2 group transition-all"
                                style={{ border: '1px solid var(--border-default)', cursor: 'grab' }}
                                title={`${item.name}\n${itemCategory}`}
                            >
                                <p className="text-xs font-medium leading-tight" style={{ color: 'var(--text-primary)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                    {item.name}
                                </p>
                                {itemCategory && (
                                    <span
                                        className="self-start text-xs px-1.5 py-0.5 rounded"
                                        style={{
                                            backgroundColor: 'var(--info-soft)',
                                            color: 'var(--info)',
                                            border: '1px solid color-mix(in srgb, var(--info) 25%, transparent)',
                                            fontWeight: 500,
                                            maxWidth: '100%',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            display: 'block',
                                        }}
                                    >
                                        {itemCategory}
                                    </span>
                                )}
                            </div>
                            );
                        })}
                    </div>
                    {pageInfo?.hasNextPage && (
                        <button
                            className="mt-2 w-full text-xs py-2 rounded-lg transition-colors"
                            style={{ background: 'none', border: '1px solid var(--border-default)', color: 'var(--text-secondary)', cursor: 'pointer' }}
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                            onClick={() => fetchMore({
                                variables: { after: pageInfo.endCursor },
                                updateQuery: (prev, { fetchMoreResult }) => {
                                    if (!fetchMoreResult) return prev;
                                    const prevNodes = prev.Library?.contents?.nodes ?? [];
                                    const nextNodes = fetchMoreResult.Library?.contents?.nodes ?? [];
                                    return {
                                        ...fetchMoreResult,
                                        Library: {
                                            ...fetchMoreResult.Library,
                                            contents: {
                                                ...fetchMoreResult.Library?.contents,
                                                nodes: [...prevNodes, ...nextNodes],
                                            },
                                        },
                                    } as any;
                                },
                            })}
                        >
                            Load more
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Inline editable input used for category/key name entry            */
/* ------------------------------------------------------------------ */
type InlineInputProps = {
    placeholder: string;
    onCommit: (value: string) => void;
    onCancel: () => void;
    indent?: boolean;
};

const InlineInput = ({ placeholder, onCommit, onCancel, indent }: InlineInputProps) => {
    const [value, setValue] = useState('');
    return (
        <div
            className="flex items-center gap-2 px-3 py-2 rounded-lg"
            style={{
                marginLeft: indent ? 20 : 0,
                border: '1px dashed var(--accent)',
                backgroundColor: 'var(--accent-soft)',
            }}
        >
            <input
                autoFocus
                className="flex-1 bg-transparent border-none outline-none text-sm"
                style={{ color: 'var(--text-primary)' }}
                placeholder={placeholder}
                value={value}
                onChange={e => setValue(e.target.value)}
                onKeyDown={e => {
                    if (e.key === 'Enter' && value.trim()) onCommit(value.trim());
                    if (e.key === 'Escape') onCancel();
                }}
            />
            <button
                onClick={() => { if (value.trim()) onCommit(value.trim()); }}
                disabled={!value.trim()}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', padding: 2 }}
            >
                <Check size={14} />
            </button>
            <button
                onClick={onCancel}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 2 }}
            >
                <X size={14} />
            </button>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Paste-from-Excel import modal                                     */
/* ------------------------------------------------------------------ */

type PasteRow = { category: string; key: string; isDuplicate: boolean };

type PasteImportModalProps = {
    mapping: KeyScheduleMapping;
    onImport: (rows: PasteRow[]) => void;
    onClose: () => void;
};

const PasteImportModal = ({ mapping, onImport, onClose }: PasteImportModalProps) => {
    const [text, setText] = useState('');

    const rows = useMemo((): PasteRow[] => {
        if (!text.trim()) return [];
        return text
            .split(/\r?\n/)
            .map(line => line.split('\t'))
            .filter(cols => cols.length >= 2)
            .map(cols => ({ category: cols[0].trim(), key: cols[1].trim() }))
            .filter(r => r.category && r.key)
            // skip a detected header row
            .filter(r => !(
                r.category.toLowerCase() === 'category' &&
                r.key.toLowerCase().replace(/\s+/g, '') === 'keyname'
            ))
            .map(r => ({
                ...r,
                isDuplicate: mapping[r.category]?.[r.key] !== undefined,
            }));
    }, [text, mapping]);

    const newRows = rows.filter(r => !r.isDuplicate);
    const dupCount = rows.length - newRows.length;

    return (
        <div
            style={{
                position: 'fixed', inset: 0,
                backgroundColor: 'rgba(0,0,0,0.45)',
                zIndex: 50,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="card"
                style={{
                    width: 520, maxHeight: '80vh',
                    display: 'flex', flexDirection: 'column',
                    border: '1px solid var(--border-default)',
                    boxShadow: 'var(--shadow-lg, 0 8px 30px rgba(0,0,0,.2))',
                }}
            >
                {/* Header */}
                <div
                    className="flex items-center gap-2 px-4 py-3 border-b flex-shrink-0"
                    style={{ borderColor: 'var(--border-default)' }}
                >
                    <ClipboardList size={15} style={{ color: 'var(--accent)' }} />
                    <span className="text-sm font-semibold flex-1" style={{ color: 'var(--text-primary)' }}>
                        Paste from Excel
                    </span>
                    <button
                        onClick={onClose}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 2 }}
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-4 flex flex-col gap-3 overflow-hidden flex-1">
                    <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                        Copy two columns from Excel — <strong>Category</strong> and <strong>Key Name</strong> — then paste below.
                        A header row is detected and skipped automatically.
                    </p>
                    <textarea
                        autoFocus
                        className="input-base w-full"
                        style={{ fontFamily: 'monospace', fontSize: 12, resize: 'vertical', minHeight: 110 }}
                        placeholder={'Category\tKey Name\nDoors\tHW-01\nDoors\tHW-02'}
                        value={text}
                        onChange={e => setText(e.target.value)}
                    />

                    {rows.length > 0 && (
                        <div
                            className="overflow-auto flex-shrink-0"
                            style={{ maxHeight: 200, border: '1px solid var(--border-default)', borderRadius: 6 }}
                        >
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                    <tr style={{ position: 'sticky', top: 0, backgroundColor: 'var(--surface-1, var(--surface-100))' }}>
                                        <th style={{ padding: '5px 10px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 600 }}>Category</th>
                                        <th style={{ padding: '5px 10px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 600 }}>Key Name</th>
                                        <th style={{ padding: '5px 10px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 600 }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => (
                                        <tr
                                            key={i}
                                            style={{
                                                borderTop: '1px solid var(--border-subtle)',
                                                backgroundColor: r.isDuplicate ? 'var(--warning-soft, #fff8e9)' : undefined,
                                            }}
                                        >
                                            <td style={{ padding: '4px 10px', color: 'var(--text-primary)' }}>{r.category}</td>
                                            <td style={{ padding: '4px 10px', color: 'var(--text-primary)' }}>{r.key}</td>
                                            <td style={{ padding: '4px 10px' }}>
                                                {r.isDuplicate
                                                    ? <span style={{ color: 'var(--warning, #b45309)' }}>Already exists</span>
                                                    : <span style={{ color: 'var(--accent)' }}>New</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {rows.length > 0 && (
                        <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                            {newRows.length} new row{newRows.length !== 1 ? 's' : ''} to add
                            {dupCount > 0 ? `, ${dupCount} duplicate${dupCount !== 1 ? 's' : ''} skipped` : ''}.
                        </p>
                    )}
                </div>

                {/* Footer */}
                <div
                    className="flex items-center justify-end gap-2 px-4 py-3 border-t flex-shrink-0"
                    style={{ borderColor: 'var(--border-default)' }}
                >
                    <button className="btn-secondary text-sm" onClick={onClose}>Cancel</button>
                    <button
                        className="btn-primary text-sm"
                        disabled={newRows.length === 0}
                        style={{ opacity: newRows.length === 0 ? 0.5 : 1 }}
                        onClick={() => { onImport(newRows); onClose(); }}
                    >
                        Import {newRows.length > 0 ? `${newRows.length} row${newRows.length !== 1 ? 's' : ''}` : ''}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

const KeyScheduleMappingStandard = ({ projectId, libraryId, mappingId, mappingName, canEdit = false, style, onBack }: KeyScheduleMappingProps) => {
    const [mapping, setMapping] = useState<KeyScheduleMapping>({});
    const [editableName, setEditableName] = useState(mappingName);
    /** reference key → display name */
    const [contentNames, setContentNames] = useState<Map<string, string>>(new Map());
    const [resolvedReferences, setResolvedReferences] = useState<Set<string>>(new Set());
    const [legacyContentIds, setLegacyContentIds] = useState<Set<string>>(new Set());
    const [contentLookupComplete, setContentLookupComplete] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingBlob, setIsLoadingBlob] = useState(false);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [dragOverKey, setDragOverKey] = useState<{ category: string; key: string; blocked: boolean } | null>(null);
    const [addingCategory, setAddingCategory] = useState(false);
    const [addingKeyFor, setAddingKeyFor] = useState<string | null>(null);
    const [showPasteModal, setShowPasteModal] = useState(false);

    const download = useDownloadAttachment();
    const httpClient = useLoadHttpClientEmpty();

    // Clear any lingering drop highlight when a drag operation ends anywhere on the page
    useEffect(() => {
        const clear = () => setDragOverKey(null);
        document.addEventListener('dragend', clear);
        return () => document.removeEventListener('dragend', clear);
    }, []);

    /* ── Query ──────────────────────────────────────────────────── */
    const { data: queryData, loading: queryLoading, refetch } = useQuery(
        GET_PROJECT_KEY_SCHEDULE_DATA,
        { variables: { id: projectId }, skip: !projectId, fetchPolicy: 'network-only' },
    );

    const [setDynamicDataNameMutation] = useMutation(SET_PROJECT_DYNAMIC_DATA_NAME);
    const [fetchContentNames] = useLazyQuery(GET_MAPPED_ITEM_NAMES);

    const existingEntry = useMemo(
        () =>
            (queryData?.Project?.dynamicData ?? []).find(
                (d: any) => d?.id === mappingId,
            ) as { id: string; name: string; type: string; data?: string; setData?: string } | undefined,
        [mappingId, queryData],
    );

    useEffect(() => setEditableName(mappingName), [mappingName]);

    /* ── Load blob when query resolves ─────────────────────────── */
    useEffect(() => {
        if (queryLoading || !existingEntry?.data) return;
        setIsLoadingBlob(true);
        setContentLookupComplete(false);
        download(existingEntry.data)
            .then(raw => {
                const str = typeof raw === 'string' ? raw : JSON.stringify(raw);
                const parsed = JSON.parse(str) as KeyScheduleMapping;
                setMapping(parsed);
                setExpandedCategories(new Set(Object.keys(parsed)));

                const allReferences = Object.values(parsed).flatMap(keys => Object.values(keys).flat());
                if (allReferences.length > 0) {
                    fetchContentNames({ variables: { libraryId } })
                        .then(result => {
                            const libraryNodes = (result.data?.Library?.contents?.nodes ?? []) as Array<{
                                id?: string | null;
                                name?: string | null;
                                types?: Array<{ id?: string | null; name?: string | null } | null> | null;
                            } | null>;
                            const names = new Map<string, string>();
                            const references = new Set<string>();
                            const legacyIds = new Set<string>();
                            for (const content of libraryNodes) {
                                if (content?.id) {
                                    references.add(getReferenceKey(content.id));
                                    legacyIds.add(content.id);
                                    if (content.name) names.set(getReferenceKey(content.id), content.name);
                                }
                                for (const type of content?.types ?? []) {
                                    if (!type?.id) continue;
                                    references.add(getReferenceKey(type.id));
                                    if (type.name) names.set(getReferenceKey(type.id), `${content?.name ?? 'Content'} · ${type.name}`);
                                    if (content?.id && type.name) {
                                        const reference: ContentTypeReference = { contentId: content.id, typeName: type.name };
                                        references.add(getReferenceKey(reference));
                                        names.set(getReferenceKey(reference), `${content.name ?? 'Content'} · ${type.name}`);
                                    }
                                }
                            }
                            setContentNames(names);
                            setResolvedReferences(references);
                            setLegacyContentIds(legacyIds);
                            setContentLookupComplete(true);
                        })
                        .catch(() => setContentLookupComplete(false));
                } else {
                    setContentNames(new Map());
                    setResolvedReferences(new Set());
                    setLegacyContentIds(new Set());
                    setContentLookupComplete(true);
                }
            })
            .catch(() => {})
            .finally(() => setIsLoadingBlob(false));
    // only re-run when the SAS download URL changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingEntry?.data, queryLoading]);

    /* ── Save ───────────────────────────────────────────────────── */
    const handleSave = useCallback(async () => {
        setIsSaving(true);
        setSaveStatus('idle');
        try {
            const trimmedName = editableName.trim();
            if (!trimmedName) throw new Error('A mapping name is required');

            if (!existingEntry) throw new Error('Data mapping not found');

            if (existingEntry.name !== trimmedName) {
                await setDynamicDataNameMutation({
                    variables: { projectId, id: existingEntry.id, name: trimmedName },
                });
            }

            // Always refetch for a fresh SAS upload URL
            const refetched = await refetch();
            const entry = (refetched.data?.Project?.dynamicData ?? []).find(
                (d: any) => d?.id === mappingId,
            ) as { setData?: string } | undefined;

            if (!entry?.setData) throw new Error('No upload URL returned');

            // PUT the blob — Azure Blob Storage SAS URL
            await httpClient.put(entry.setData, JSON.stringify(mapping), {
                headers: {
                    'x-ms-blob-type': 'BlockBlob',
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                },
            });

            setIsDirty(false);
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 3000);
        } catch {
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 4000);
        } finally {
            setIsSaving(false);
        }
    }, [mapping, editableName, existingEntry, projectId, setDynamicDataNameMutation, httpClient, refetch, mappingId]);

    /* ── Guard navigation when dirty ───────────────────────────── */
    const handleBack = useCallback(() => {
        if (isDirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
        onBack();
    }, [isDirty, onBack]);

    /* ── Mapping mutations ──────────────────────────────────────── */
    const addCategory = useCallback((name: string) => {
        if (mapping[name] !== undefined) return;
        setMapping(prev => ({ ...prev, [name]: {} }));
        setExpandedCategories(prev => new Set([...Array.from(prev), name]));
        setIsDirty(true);
    }, [mapping]);

    const removeCategory = useCallback((category: string) => {
        setMapping(prev => {
            const next = { ...prev };
            delete next[category];
            return next;
        });
        setIsDirty(true);
    }, []);

    const addKey = useCallback((category: string, keyName: string) => {
        if (mapping[category]?.[keyName] !== undefined) return;
        setMapping(prev => ({
            ...prev,
            [category]: { ...(prev[category] ?? {}), [keyName]: [] },
        }));
        setIsDirty(true);
    }, [mapping]);

    const removeKey = useCallback((category: string, keyName: string) => {
        setMapping(prev => {
            const cat = { ...(prev[category] ?? {}) };
            delete cat[keyName];
            return { ...prev, [category]: cat };
        });
        setIsDirty(true);
    }, []);

    const removeContent = useCallback((category: string, keyName: string, reference: MappingReference) => {
        setMapping(prev => ({
            ...prev,
            [category]: {
                ...(prev[category] ?? {}),
                [keyName]: (prev[category]?.[keyName] ?? []).filter(item => getReferenceKey(item) !== getReferenceKey(reference)),
            },
        }));
        setIsDirty(true);
    }, []);

    const handlePasteImport = useCallback((rows: PasteRow[]) => {
        const newCats = Array.from(new Set(rows.map(r => r.category)));
        setMapping(prev => {
            const next = { ...prev };
            for (const { category, key } of rows) {
                if (!next[category]) next[category] = {};
                if (next[category][key] === undefined) {
                    next[category] = { ...next[category], [key]: [] };
                }
            }
            return next;
        });
        setExpandedCategories(prev => {
            const next = new Set(prev);
            for (const cat of newCats) next.add(cat);
            return next;
        });
        setIsDirty(true);
    }, []);

    /* ── Drop handlers on key rows ──────────────────────────────── */
    const handleKeyDragOver = (e: React.DragEvent, category: string, keyName: string) => {
        if (!e.dataTransfer.types.includes('application/x-content-reference')) return;
        const catMatches = !currentDragCategory ||
            currentDragCategory.toLowerCase() === category.toLowerCase();
        if (catMatches) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
        }
        setDragOverKey({ category, key: keyName, blocked: !catMatches });
    };

    const handleKeyDragLeave = (e: React.DragEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDragOverKey(null);
        }
    };

    const handleKeyDrop = (e: React.DragEvent, category: string, keyName: string) => {
        e.preventDefault();
        setDragOverKey(null);
        const referenceJson = e.dataTransfer.getData('application/x-content-reference');
        const contentName = e.dataTransfer.getData('application/x-content-name');
        if (!referenceJson) return;
        const reference = JSON.parse(referenceJson) as ContentTypeReference;
        if (!isContentTypeReference(reference)) return;
        const referenceKey = getReferenceKey(reference);
        // Reject if dragged content's category doesn't match this mapping category
        if (currentDragCategory && currentDragCategory.toLowerCase() !== category.toLowerCase()) return;
        if (contentName) {
            setContentNames(prev => new Map(prev).set(referenceKey, contentName));
        }
        setResolvedReferences(prev => new Set(prev).add(referenceKey));
        if (!(mapping[category]?.[keyName] ?? []).some(item => getReferenceKey(item) === referenceKey)) {
            setMapping(prev => ({
                ...prev,
                [category]: {
                    ...(prev[category] ?? {}),
                    [keyName]: [...(prev[category]?.[keyName] ?? []), reference],
                },
            }));
            setIsDirty(true);
        }
    };

    /* ── Helpers ────────────────────────────────────────────────── */
    const toggleCategory = (category: string) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(category)) next.delete(category);
            else next.add(category);
            return next;
        });
    };

    const categories = Object.keys(mapping);
    const isLoading = queryLoading || isLoadingBlob;
    const unavailableContentCount = contentLookupComplete
        ? Array.from(new Set(
            Object.values(mapping).flatMap(keys => Object.values(keys).flat()),
        )).filter(reference => !resolvedReferences.has(getReferenceKey(reference))).length
        : 0;

    /* ── Render ─────────────────────────────────────────────────── */
    return (
        <div className="flex flex-col h-full" style={style} data-testid="ContentDataMapping">

            {/* ─── Toolbar ───────────────────────────────────────── */}
            <div
                className="flex items-center gap-3 px-4 py-2.5 border-b flex-shrink-0"
                style={{ borderColor: 'var(--border-default)' }}
            >
                <button
                    className="flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-md transition-colors"
                    style={{ color: 'var(--accent-text)', background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={handleBack}
                >
                    <ArrowLeft size={15} /> Back
                </button>
                {canEdit ? (
                    <input
                        aria-label="Mapping name"
                        className="text-sm font-semibold px-2 py-1 rounded-md"
                        style={{ color: 'var(--text-primary)', border: '1px solid var(--border-default)', background: 'var(--bg-card)', width: 240 }}
                        value={editableName}
                        onChange={event => { setEditableName(event.target.value); setIsDirty(true); }}
                    />
                ) : (
                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{editableName}</span>
                )}
                <div className="flex-1" />
                {saveStatus === 'saved' && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--success)' }}>
                        <Check size={13} /> Saved
                    </span>
                )}
                {saveStatus === 'error' && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--danger)' }}>
                        <AlertCircle size={13} /> Save failed
                    </span>
                )}
                {isDirty && saveStatus === 'idle' && (
                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Unsaved changes</span>
                )}
                <button
                    className="btn-primary flex items-center gap-2 text-sm"
                    disabled={!isDirty || isSaving}
                    onClick={handleSave}
                    style={{ opacity: !isDirty || isSaving ? 0.5 : 1 }}
                >
                    {isSaving
                        ? <Loader2 size={14} className="animate-spin" />
                        : <Save size={14} />}
                    Save
                </button>
            </div>

            {/* ─── Split panes ─────────────────────────────────── */}
            <div className="flex flex-1 min-h-0">

                {/* ── Left: data mapping tree ────────────────────── */}
                <div
                    className="flex flex-col min-h-0 border-r"
                    style={{ flex: '0 0 42%', borderColor: 'var(--border-default)' }}
                >
                    {/* Pane header */}
                    <div
                        className="flex items-center gap-2 px-3 py-2 border-b flex-shrink-0"
                        style={{ borderColor: 'var(--border-default)', backgroundColor: 'var(--surface-1)' }}
                    >
                        <span className="text-xs font-semibold uppercase tracking-wider flex-1" style={{ color: 'var(--text-tertiary)' }}>
                            Data Mapping
                        </span>
                        {unavailableContentCount > 0 && (
                            <span
                                className="inline-flex items-center gap-1 text-xs"
                                style={{ color: 'var(--danger, #ef4444)' }}
                                title="These mapped content types no longer exist or are unavailable"
                            >
                                <AlertCircle size={12} /> {unavailableContentCount} unavailable
                            </span>
                        )}
                        <button
                            className="flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors"
                            style={{
                                background: 'none',
                                border: '1px solid var(--border-default)',
                                color: 'var(--text-secondary)',
                                cursor: 'pointer',
                            }}
                            onClick={() => setShowPasteModal(true)}
                        >
                            <ClipboardList size={11} /> Paste
                        </button>
                        <button
                            className="flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors"
                            style={{
                                background: 'none',
                                border: '1px solid var(--border-default)',
                                color: 'var(--text-secondary)',
                                cursor: 'pointer',
                            }}
                            onClick={() => setAddingCategory(true)}
                        >
                            <Plus size={11} /> Category
                        </button>
                    </div>

                    {/* Category list */}
                    <div className="flex-1 overflow-auto p-3 space-y-2">

                        {/* Loading */}
                        {isLoading && (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                            </div>
                        )}

                        {/* Empty state */}
                        {!isLoading && categories.length === 0 && !addingCategory && (
                            <div className="flex flex-col items-center justify-center py-12 gap-3">
                                <Tag size={32} style={{ color: 'var(--text-tertiary)', opacity: 0.4 }} />
                                <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No categories yet</p>
                                <button
                                    className="text-xs font-medium"
                                    style={{ color: 'var(--accent-text)', background: 'none', border: 'none', cursor: 'pointer' }}
                                    onClick={() => setAddingCategory(true)}
                                >
                                    + Add your first category
                                </button>
                            </div>
                        )}

                        {/* New category input */}
                        {addingCategory && (
                            <InlineInput
                                placeholder="Category name (e.g. Doors)"
                                onCommit={name => { addCategory(name); setAddingCategory(false); }}
                                onCancel={() => setAddingCategory(false)}
                            />
                        )}

                        {/* Categories */}
                        {!isLoading && categories.map(category => {
                            const isExpanded = expandedCategories.has(category);
                            const keys = Object.keys(mapping[category] ?? {});
                            return (
                                <div
                                    key={category}
                                    className="rounded-lg overflow-hidden"
                                    style={{ border: '1px solid var(--border-default)' }}
                                >
                                    {/* Category row */}
                                    <div
                                        className="flex items-center gap-2 px-3 py-2 cursor-pointer group select-none"
                                        style={{ backgroundColor: 'var(--surface-1)' }}
                                        onClick={() => toggleCategory(category)}
                                    >
                                        {isExpanded
                                            ? <ChevronDown size={13} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                                            : <ChevronRight size={13} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />}
                                        <FolderOpen size={13} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                                        <span className="text-sm font-semibold flex-1 truncate" style={{ color: 'var(--text-primary)' }}>
                                            {category}
                                        </span>
                                        <span className="text-xs shrink-0" style={{ color: 'var(--text-tertiary)' }}>
                                            {keys.length} key{keys.length !== 1 ? 's' : ''}
                                        </span>
                                        <button
                                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}
                                            title="Remove category"
                                            onClick={e => { e.stopPropagation(); removeCategory(category); }}
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>

                                    {/* Key rows */}
                                    {isExpanded && (
                                        <div>
                                            {keys.map(keyName => {
                                                const contentIds = mapping[category][keyName] ?? [];
                                                const isOver = dragOverKey?.category === category && dragOverKey?.key === keyName;
                                                const isBlocked = isOver && dragOverKey?.blocked;
                                                return (
                                                    <div
                                                        key={keyName}
                                                        className="border-t transition-colors"
                                                        style={{
                                                            borderColor: 'var(--border-subtle)',
                                                            backgroundColor: isBlocked ? 'var(--danger-soft, #fee2e2)' : isOver ? 'var(--accent-soft)' : 'var(--bg-card)',
                                                            outline: isBlocked ? '2px dashed var(--danger, #ef4444)' : isOver ? '2px dashed var(--accent)' : 'none',
                                                            outlineOffset: '-2px',
                                                        }}
                                                        onDragOver={e => handleKeyDragOver(e, category, keyName)}
                                                        onDragLeave={handleKeyDragLeave}
                                                        onDrop={e => handleKeyDrop(e, category, keyName)}
                                                    >
                                                        {/* Key name row */}
                                                        <div className="flex items-center gap-2 px-3 py-2 group">
                                                            <GripVertical size={12} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                                                            <span className="text-sm font-medium flex-1 truncate" style={{ color: 'var(--text-primary)' }}>
                                                                {keyName}
                                                            </span>
                                                            {isBlocked && (
                                                                <span className="text-xs shrink-0" style={{ color: 'var(--danger, #ef4444)' }}>
                                                                    Category mismatch
                                                                </span>
                                                            )}
                                                            {isOver && !isBlocked && (
                                                                <span className="text-xs shrink-0" style={{ color: 'var(--accent)' }}>
                                                                    Drop to assign
                                                                </span>
                                                            )}
                                                            <button
                                                                className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded"
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}
                                                                title="Remove key"
                                                                onClick={() => removeKey(category, keyName)}
                                                            >
                                                                <X size={12} />
                                                            </button>
                                                        </div>

                                                        {/* Assigned content chips */}
                                                        <div className="flex flex-wrap gap-1 px-3 pb-2 ml-4">
                                                            {contentIds.length === 0 ? (
                                                                <span className="text-xs italic" style={{ color: 'var(--text-tertiary)' }}>
                                                                    {isBlocked ? 'Wrong category' : isOver ? '↓ Release to assign' : 'Drop content here to assign'}
                                                                </span>
                                                            ) : (
                                                                contentIds.map(reference => {
                                                                    const referenceKey = getReferenceKey(reference);
                                                                    const legacyId = typeof reference === 'string' ? reference : undefined;
                                                                    const isUnavailable = contentLookupComplete && !resolvedReferences.has(referenceKey);
                                                                    const isLegacy = !!legacyId && legacyContentIds.has(legacyId);
                                                                    return (
                                                                        <span
                                                                            key={referenceKey}
                                                                            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                                                                            style={{
                                                                                backgroundColor: isUnavailable ? 'var(--danger-soft, #fee2e2)' : isLegacy ? 'var(--warning-soft)' : 'var(--accent-soft)',
                                                                                color: isUnavailable ? 'var(--danger, #ef4444)' : isLegacy ? 'var(--warning)' : 'var(--accent-text)',
                                                                                border: `1px solid ${isUnavailable ? 'var(--danger, #ef4444)' : isLegacy ? 'var(--warning)' : 'color-mix(in srgb, var(--accent) 30%, transparent)'}`,
                                                                            }}
                                                                            title={isUnavailable ? `Content type unavailable (${isContentTypeReference(reference) ? `${reference.contentId} / ${reference.typeName}` : reference})` : isLegacy ? 'Legacy content-level assignment; applies to all current types' : contentNames.get(referenceKey)}
                                                                        >
                                                                            {isUnavailable && <AlertCircle size={10} />}
                                                                            {isUnavailable ? `Unavailable · ${isContentTypeReference(reference) ? reference.typeName : `${reference.substring(0, 8)}…`}` : isLegacy ? `Legacy · ${contentNames.get(referenceKey) ?? `${legacyId?.substring(0, 8)}…`}` : contentNames.get(referenceKey) ?? (isContentTypeReference(reference) ? reference.typeName : `${reference.substring(0, 8)}…`)}
                                                                            <button
                                                                                onClick={() => removeContent(category, keyName, reference)}
                                                                                title="Remove"
                                                                                style={{
                                                                                    background: 'none',
                                                                                    border: 'none',
                                                                                    cursor: 'pointer',
                                                                                    padding: 0,
                                                                                    color: 'inherit',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                }}
                                                                            >
                                                                                <X size={10} />
                                                                            </button>
                                                                        </span>
                                                                    );
                                                                })
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {/* Add key row */}
                                            {addingKeyFor === category ? (
                                                <div className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                                                    <div className="px-3 py-2">
                                                        <InlineInput
                                                            placeholder="Key name (e.g. HW-01)"
                                                            onCommit={k => { addKey(category, k); setAddingKeyFor(null); }}
                                                            onCancel={() => setAddingKeyFor(null)}
                                                            indent
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <button
                                                    className="w-full flex items-center gap-2 px-3 py-2 text-xs border-t transition-colors"
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        borderTop: '1px solid var(--border-subtle)',
                                                        cursor: 'pointer',
                                                        color: 'var(--text-tertiary)',
                                                    }}
                                                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                    onClick={() => {
                                                        setAddingKeyFor(category);
                                                        setExpandedCategories(prev => new Set([...Array.from(prev), category]));
                                                    }}
                                                >
                                                    <Plus size={11} /> Add Key
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ── Right: Content library (drag source) ──────── */}
                <div className="flex flex-col flex-1 min-w-0 min-h-0">
                    <div
                        className="flex items-center gap-2 px-3 py-2 border-b flex-shrink-0"
                        style={{ borderColor: 'var(--border-default)', backgroundColor: 'var(--surface-1)' }}
                    >
                        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                            Library
                        </span>
                        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                            — drag a card onto a key to assign it
                        </span>
                    </div>
                    <div className="flex-1 overflow-hidden p-3">
                        {libraryId ? (
                            <LibraryContentPicker libraryId={libraryId} />
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full gap-2">
                                <Package size={28} style={{ color: 'var(--text-tertiary)', opacity: 0.4 }} />
                                <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No library assigned to this project</p>
                            </div>
                        )}
                    </div>
                </div>

            </div>

            {showPasteModal && (
                <PasteImportModal
                    mapping={mapping}
                    onImport={handlePasteImport}
                    onClose={() => setShowPasteModal(false)}
                />
            )}
        </div>
    );
};

export default KeyScheduleMappingStandard;
